import React, { createContext, useContext, useState, useEffect } from "react";

const TranslationContext = createContext();

export function TranslationProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [isLoading, setIsLoading] = useState(true);

  // Helper to get raw cookies
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  // Helper to set cookie
  const setCookie = (name, value, days = 365) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `; expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value}${expires}; path=/;`;
  };

  // Initialize language detection & load Google Translate
  useEffect(() => {
    const initializeLanguage = async () => {
      let preferredLang = localStorage.getItem("preferred_language");

      if (!preferredLang) {
        // 1. Detect browser language first (instant)
        const browserLang = navigator.language || navigator.userLanguage;
        if (browserLang) {
          const mainBrowserLang = browserLang.split("-")[0].toLowerCase();
          if (mainBrowserLang !== "en") {
            preferredLang = mainBrowserLang;
          }
        }

        // 2. Fallback to geolocation native language if browser is English but visitor is abroad
        if (!preferredLang) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);
            const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
            clearTimeout(timeoutId);
            if (res.ok) {
              const data = await res.json();
              if (data.languages) {
                const geoLang = data.languages.split(",")[0].split("-")[0].toLowerCase();
                if (geoLang && geoLang !== "en") {
                  preferredLang = geoLang;
                }
              }
            }
          } catch (err) {
            console.warn("Geolocation language detection failed:", err);
          }
        }

        preferredLang = preferredLang || "en";
        localStorage.setItem("preferred_language", preferredLang);
      }

      setCurrentLanguage(preferredLang);

      // Verify and set googtrans cookie for Google Translate
      const currentCookie = getCookie("googtrans");
      const expectedCookieValue = preferredLang === "en" ? "/en/en" : `/en/${preferredLang}`;

      if (currentCookie !== expectedCookieValue) {
        setCookie("googtrans", expectedCookieValue);
        // Force reload to apply cookie values immediately on first load
        window.location.reload();
        return;
      }

      setIsLoading(false);
    };

    initializeLanguage();

    // Dynamically inject the Google Translate initialization function
    window.googleTranslateElementInit = () => {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
      }
    };

    // Load Google Translate script if not already loaded
    if (!document.getElementById("google-translate-script")) {
      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const changeLanguage = (lang) => {
    if (lang === currentLanguage) return;
    setIsLoading(true);

    localStorage.setItem("preferred_language", lang);
    setCurrentLanguage(lang);

    const expectedCookieValue = lang === "en" ? "/en/en" : `/en/${lang}`;
    setCookie("googtrans", expectedCookieValue);

    window.location.reload();
  };

  return (
    <TranslationContext.Provider value={{ currentLanguage, changeLanguage, isLoading }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useAppTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useAppTranslation must be used within a TranslationProvider");
  }
  return context;
}
